import React, { useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from './Button';
import { X, RotateCcw, Check } from 'lucide-react';

interface SignaturePanelProps {
  onSave: (signature: string) => void;
  onCancel?: () => void;
  initialSignature?: string;
  required?: boolean;
}

export const SignaturePanel: React.FC<SignaturePanelProps> = ({
  onSave,
  onCancel,
  initialSignature,
  required = false,
}) => {
  const signatureRef = useRef<SignatureCanvas>(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [isSaved, setIsSaved] = useState(false);

  const handleClear = () => {
    signatureRef.current?.clear();
    setIsEmpty(true);
    setIsSaved(false);
  };

  const handleSave = () => {
    if (signatureRef.current?.isEmpty()) {
      alert('Please provide your signature before continuing');
      return;
    }

    const dataUrl = signatureRef.current?.toDataURL('image/png');
    if (dataUrl) {
      onSave(dataUrl);
      setIsSaved(true);
      // Auto-hide success message after 3 seconds
      setTimeout(() => setIsSaved(false), 3000);
    }
  };

  const handleBegin = () => {
    setIsEmpty(false);
    setIsSaved(false);
  };

  return (
    <div className="signature-panel">
      <div className="signature-panel-header">
        <h4>Your Signature {required && <span className="required">*</span>}</h4>
        <p>Please sign in the box below using your mouse or touch screen</p>
        {isSaved && (
          <div className="signature-saved-message">
            <Check size={16} />
            <span>Signature saved successfully!</span>
          </div>
        )}
      </div>

      <div className="signature-canvas-container">
        <SignatureCanvas
          ref={signatureRef}
          canvasProps={{
            className: 'signature-canvas',
            width: 600,
            height: 200,
          }}
          onBegin={handleBegin}
        />
        {isEmpty && !initialSignature && (
          <div className="signature-placeholder">Sign here</div>
        )}
      </div>

      <div className="signature-actions">
        <Button
          type="button"
          onClick={handleClear}
          variant="secondary"
          size="sm"
          disabled={isEmpty}
        >
          <RotateCcw size={16} />
          Clear
        </Button>
        <div className="signature-actions-right">
          {onCancel && (
            <Button type="button" onClick={onCancel} variant="secondary" size="sm">
              <X size={16} />
              Cancel
            </Button>
          )}
          <Button
            type="button"
            onClick={handleSave}
            variant="primary"
            size="sm"
            disabled={isEmpty && !initialSignature}
          >
            <Check size={16} />
            Save Signature
          </Button>
        </div>
      </div>

      <style>{`
        .signature-panel {
          background: var(--color-background-paper);
          border: 1px solid var(--color-border);
          border-radius: 8px;
          padding: 1.5rem;
          margin: 1.5rem 0;
        }

        .signature-panel-header {
          margin-bottom: 1rem;
        }

        .signature-panel-header h4 {
          font-size: 1.125rem;
          font-weight: 600;
          color: var(--color-text-primary);
          margin: 0 0 0.5rem 0;
        }

        .signature-panel-header h4 .required {
          color: var(--color-error);
          margin-left: 0.25rem;
        }

        .signature-panel-header p {
          font-size: 0.875rem;
          color: var(--color-text-secondary);
          margin: 0;
        }

        .signature-saved-message {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-top: 0.75rem;
          padding: 0.75rem 1rem;
          background: #d4edda;
          border: 1px solid #c3e6cb;
          border-radius: 6px;
          color: #155724;
          font-size: 0.875rem;
          font-weight: 500;
          animation: slideDown 0.3s ease-out;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .signature-canvas-container {
          position: relative;
          border: 2px dashed var(--color-border);
          border-radius: 6px;
          background: white;
          margin-bottom: 1rem;
          overflow: hidden;
        }

        .signature-canvas {
          display: block;
          max-width: 100%;
          height: auto;
          cursor: crosshair;
        }

        .signature-placeholder {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: var(--color-text-tertiary);
          font-size: 1.25rem;
          font-style: italic;
          pointer-events: none;
          user-select: none;
        }

        .signature-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 0.75rem;
        }

        .signature-actions-right {
          display: flex;
          gap: 0.75rem;
        }

        /* Tablet Responsive */
        @media (max-width: 1024px) {
          .signature-canvas-container canvas {
            width: 100% !important;
            height: auto !important;
          }
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .signature-panel {
            padding: 1.25rem;
          }

          .signature-panel-header h4 {
            font-size: 1rem;
          }

          .signature-panel-header p {
            font-size: 0.8125rem;
          }

          .signature-canvas-container {
            border-width: 1px;
          }

          .signature-canvas-container canvas {
            width: 100% !important;
            height: 150px !important;
          }

          .signature-placeholder {
            font-size: 1rem;
          }

          .signature-actions {
            flex-direction: column;
            gap: 0.5rem;
          }

          .signature-actions button {
            width: 100%;
          }

          .signature-actions-right {
            width: 100%;
            flex-direction: column;
          }
        }

        /* Small Mobile */
        @media (max-width: 480px) {
          .signature-panel {
            padding: 1rem;
            margin: 1rem 0;
          }

          .signature-canvas-container canvas {
            height: 120px !important;
          }

          .signature-placeholder {
            font-size: 0.875rem;
          }
        }
      `}</style>
    </div>
  );
};
